import { useEffect, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row
} from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import TableContainer from '../../../Components/Common/TableContainer';
import { CsvExport } from '../../../Components/Custom/CsvExport';
import { PageHeader } from '../../../Components/Custom/PageHeader';
import { Pagination } from '../../../Components/Custom/pagination';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
  dateForExport,
  paramsForExport,
  setDocumentTitle
} from '../../../helpers/helpers';
import { columns } from './columns';
import { Filters } from './filters';
import {
  exportHeaders,
  exportUsers,
  getUsers,
  removeUsersForExport
} from './slice';
import { Modal } from '../../../Components/Custom/modal';

const PAGE_TITLE = 'Users';

const INITIAL_PARAMS: {
  sortBy: string;
  sortOrder: string;
  countPerPage: string;
  numberOfPage: string;
} = {
  sortBy: 'dateCreated',
  sortOrder: 'DESC',
  countPerPage: '10',
  numberOfPage: '1'
};

const UsersPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams(INITIAL_PARAMS);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [exportEmail, setExportEmail] = useState<string>('');

  const { loading, data, totalCount, loadingExport, dataForExport } =
    useAppSelector((state) => state.users);

  const handleClearState = () => {
    setIsOpen(!isOpen);
    setExportEmail('');
    setSearchParams({});
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    const params = paramsForExport(searchParams);

    params.set('exportEmail', exportEmail);

    dispatch(exportUsers(params));

    handleClearState();
  };

  useEffect(() => {
    dispatch(getUsers(searchParams));
  }, [searchParams]);

  setDocumentTitle(PAGE_TITLE);

  return (
    <div className='page-content'>
      <Container fluid>
        <BreadCrumb title={PAGE_TITLE} pageTitle='Dashboard' />
        <Row>
          <Col lg={12}>
            <Card id='orderList'>
              <PageHeader
                loading={loading || loadingExport}
                initialParams={INITIAL_PARAMS}
                setSearchParams={setSearchParams}
                title={PAGE_TITLE}
                actions={
                  <>
                    <CsvExport
                      action={() =>
                        dispatch(exportUsers(paramsForExport(searchParams)))
                      }
                      data={dataForExport}
                      headers={exportHeaders}
                      disabled={
                        loading ||
                        loadingExport ||
                        data?.length === 0 ||
                        totalCount > 40000
                      }
                      fileName={`users-${dateForExport}`}
                      removeDataAction={removeUsersForExport}
                      buttonName='Export filtered data'
                    />
                    <Button
                      color='primary'
                      disabled={loading || loadingExport}
                      onClick={() => setIsOpen(!isOpen)}>
                      Export all users to email
                    </Button>
                  </>
                }
              />
              <CardBody className='pt-0'>
                <Filters
                  params={searchParams}
                  setParams={setSearchParams}
                  initialParams={INITIAL_PARAMS}
                />

                <TableContainer
                  columns={columns}
                  data={data || []}
                  divClass='table-responsive table-card mb-1'
                  tableClass='align-middle table-nowrap'
                  theadClass='table-light text-muted'
                />

                <Pagination
                  totalCount={totalCount}
                  data={data}
                  params={searchParams}
                  setParams={setSearchParams}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal
        isOpen={isOpen}
        setIsOpen={() => setIsOpen(!isOpen)}
        title='Add Email'
        size='m'
        form
        onFormSubmit={handleSubmit}
        actions={
          <>
            <Button color='primary' onClick={handleClearState}>
              Cancel
            </Button>
            <Button color='primary' type='submit' disabled={!exportEmail}>
              Add
            </Button>
          </>
        }>
        <Col lg={12}>
          <Label className='w-100 text-left' for='userId'>
            Enter email
          </Label>
          <Input
            id='userId'
            className='w-100 mb-2'
            type='text'
            value={exportEmail}
            onChange={(e) => setExportEmail(e.target.value)}
            placeholder='Enter here'
          />
        </Col>
      </Modal>
    </div>
  );
};

export default UsersPage;
